import React, { useEffect, useState, useMemo } from 'react';
import { PageRenderer } from '@/components/pagebuilder/PageRenderer';
import { resolveApiBase } from '@/lib/utils';

export default function PublicPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const slug = window.location.pathname.split('/').pop();

  useEffect(() => {
    let mounted = true;
    const API = resolveApiBase();
    fetch(`${API}/pages/slug/${slug}`)
      .then(r => r.json())
      .then(json => {
        if (!mounted) return;
        if (json.error) setError(json.error); else setData(json);
      })
      .catch(e => setError(e.message));
    return () => { mounted = false; };
  }, [slug]);

  // Fire page view tracking once per slug load
  useEffect(() => {
    if (!slug) return;
    const source = slug.endsWith('-code') ? 'code' : 'landing';
    const API = resolveApiBase();
    fetch(`${API}/public/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug, source }),
    }).catch(() => {});
  }, [slug]);

  // Always compute hooks before any early returns to keep hook order stable
  const replacedTree = useMemo(() => {
    if (!data || !data.page) return null;
    const action = data.action || {};
    const company = data.company || {};
    const tokens = {
      '{{action.title}}': action.title || '',
      '{{action.discountType}}': action.discountType || '',
      '{{action.discountValue}}': action.discountValue != null ? String(action.discountValue) : '',
      '{{action.companyName}}': (data.company && data.company.name) || '',
      '{{action.campaignName}}': action.campaignName || '',
      '{{company.addressLine1}}': company.addressLine1 || '',
      '{{company.addressLine2}}': company.addressLine2 || '',
      '{{company.postalCode}}': company.postalCode || '',
      '{{company.city}}': company.city || '',
      '{{company.country}}': company.country || '',
    };
    const str = JSON.stringify(data.page);
    const replaced = Object.keys(tokens).reduce((acc, k) => acc.split(k).join(tokens[k]), str);
    try { return JSON.parse(replaced); } catch { return data.page; }
  }, [data]);

  if (error) return <div className="p-6 text-red-600">{error}</div>;
  if (!data) return <div className="p-6 text-sm text-slate-500">Lade Seite...</div>;

  // Theme injection (colors + Google Fonts)
  if (data.theme) {
    const styleElId = 'pb-theme';
    let el = document.getElementById(styleElId);
    if (!el) { el = document.createElement('style'); el.id = styleElId; document.head.appendChild(el); }
    const colors = data.theme.colors || {};
    const headingFont = data.theme.typography?.headingFont || '';
    const bodyFont = data.theme.typography?.bodyFont || '';
    // Build CSS with variables + defaults
    const bodyStack = `var(--pb-font-body, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif)`;
    const headingStack = `var(--pb-font-heading, inherit)`;
    el.innerHTML = `:root{--pb-primary:${colors.primary || '#2563eb'};--pb-surface:${colors.surface || '#ffffff'};${bodyFont?`--pb-font-body:'${bodyFont}', system-ui;`:''}${headingFont?`--pb-font-heading:'${headingFont}', ${bodyFont || 'system-ui'};`:''}}
      .pb-root{ font-family: ${bodyStack}; }
      .pb-root h1,.pb-root h2,.pb-root h3,.pb-root h4,.pb-root h5,.pb-root h6{ font-family: ${headingStack}; }`;
    // Load Google Fonts for both heading and body
    const addFontLink = (family, id)=>{
      if (!family) return;
      const linkId = id;
      if (document.getElementById(linkId)) return;
      const f = family.replace(/ /g, '+');
      const link = document.createElement('link');
      link.id = linkId; link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${f}:wght@400;600;700&display=swap`;
      document.head.appendChild(link);
    };
    addFontLink(headingFont, 'pb-font-link-heading');
    addFontLink(bodyFont, 'pb-font-link-body');
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 py-10">
        <h1 className="sr-only">{data.meta?.title}</h1>
        <PageRenderer tree={replacedTree} meta={{ action: data.action || null, company: data.company || null }} />
      </div>
    </div>
  );
}
